/**
 * Fetches a replay for the player, ported from avrio-rio
 * (services/workspace/replay/loader.ts): every chunk the manifest lists,
 * each checked against its SHA-256; the events read into the model; each
 * voice session's audio joined into one playable file; and the screen frames
 * taken while the candidate was away. Everything is fetched up front: the
 * signed links only last five minutes.
 *
 * Verified by Avrio. The manifest's exact bytes come from `manifestUrl` (what
 * Rio signed at `complete`), and the chunk list is read from those bytes, not
 * from the copy beside them. The replay counts as verified only when it is
 * signed, those bytes were read and list the same chunks as the view, and
 * every listed chunk loaded and matched its SHA-256. Anything less is "not
 * verified", said neutrally: a missing link, an expired one or a damaged part
 * all look the same from here. Checking the signature itself needs the
 * signing key's public half from Rio, a follow-up (avrio-rio docs/work-replay.md).
 */
import { sha256Hex } from './format';
import { readManifest } from './manifest';
import { buildReplay } from './model';
import type { ReplayData } from './model';
import type { ManifestChunk, ReplayManifest, WorkReplayView } from './types';

export interface ReplayAudio {
  session: number;
  startT: number;
  blob: Blob;
}

export interface ReplayFrameImage {
  n: number;
  blob: Blob;
}

export interface Verification {
  verified: boolean;
  /** SHA-256 of the manifest's exact bytes (what was signed), when they could be read. */
  manifestSha256: string | null;
  /** Chunks that loaded and matched, of those listed. */
  matched: number;
  total: number;
}

export interface LoadedReplay {
  replay: ReplayData;
  manifest: ReplayManifest;
  audio: ReplayAudio[];
  frames: ReplayFrameImage[];
  verification: Verification;
}

export interface LoadDeps {
  fetch?: (url: string) => Promise<{
    ok: boolean;
    status?: number;
    arrayBuffer: () => Promise<ArrayBuffer>;
  }>;
  sha256?: (bytes: Uint8Array) => Promise<string>;
  /** Called as each part arrives: `done` of `total` (the manifest counts as one). */
  onProgress?: (done: number, total: number) => void;
}

/** Screen frames are fetched this many at a time. */
const FRAME_FETCHES_AT_ONCE = 6;

export class ReplayLoadError extends Error {
  constructor() {
    super("The replay's recording couldn't be read.");
    this.name = 'ReplayLoadError';
  }
}

const sameChunks = (a: ManifestChunk[], b: ManifestChunk[]) => {
  const key = (c: ManifestChunk) => `${c.kind}:${c.n}:${c.bytes}:${c.sha256}`;
  const left = a.map(key).sort();
  const right = b.map(key).sort();
  return left.length === right.length && left.every((k, i) => k === right[i]);
};

export async function loadReplay(
  view: WorkReplayView,
  deps: LoadDeps = {},
): Promise<LoadedReplay> {
  const doFetch =
    deps.fetch ?? ((url: string) => fetch(url, { credentials: 'omit' }));
  const sha256 = deps.sha256 ?? sha256Hex;
  const total = view.manifest.chunks.length + 1;
  let done = 0;
  const tick = () => {
    done += 1;
    deps.onProgress?.(done, total);
  };

  const getBytes = async (url: string): Promise<Uint8Array | null> => {
    try {
      const response = await doFetch(url);
      if (!response.ok) return null;
      return new Uint8Array(await response.arrayBuffer());
    } catch {
      return null;
    }
  };

  // The manifest's own bytes: what Rio signed.
  let manifestSha256: string | null = null;
  let signed: ReplayManifest | null = null;
  if (view.manifestUrl) {
    const bytes = await getBytes(view.manifestUrl);
    if (bytes) {
      manifestSha256 = await sha256(bytes);
      try {
        signed = readManifest(JSON.parse(new TextDecoder().decode(bytes)));
      } catch {
        signed = null;
      }
    }
  }
  tick();
  const manifest = signed ?? view.manifest;
  const listsAgree =
    signed !== null && sameChunks(signed.chunks, view.manifest.chunks);

  let matched = 0;
  const fetchChunk = async (
    chunk: ManifestChunk,
  ): Promise<Uint8Array | null> => {
    const url = view.urls.find((u) => u.n === chunk.n && u.kind === chunk.kind);
    const body = url ? await getBytes(url.url) : null;
    if (body && (await sha256(body)) === chunk.sha256) matched += 1;
    tick();
    return body;
  };

  const byKind = (kind: string) =>
    manifest.chunks.filter((c) => c.kind === kind).sort((a, b) => a.n - b.n);

  const eventsChunks = byKind('events');
  const texts = await Promise.all(eventsChunks.map(fetchChunk));
  const decoder = new TextDecoder();
  const present = texts.filter((t): t is Uint8Array => t !== null);
  if (present.length === 0) throw new ReplayLoadError();
  const replay = buildReplay(
    present.map((t) => decoder.decode(t)),
    manifest.transcript,
  );

  const audioChunks = byKind('audio');
  const audioBodies = new Map<number, Uint8Array>();
  const audioParts = await Promise.all(audioChunks.map(fetchChunk));
  audioChunks.forEach((c, i) => {
    const body = audioParts[i];
    if (body) audioBodies.set(c.n, body);
  });
  const audio: ReplayAudio[] = [];
  for (const session of replay.voice) {
    const parts = session.chunks
      .map((n) => audioBodies.get(n))
      .filter((p): p is Uint8Array => p !== undefined);
    if (parts.length) {
      audio.push({
        session: session.session,
        startT: session.startT,
        blob: new Blob(parts as BlobPart[], { type: 'audio/webm' }),
      });
    }
  }

  const frames: ReplayFrameImage[] = [];
  const screenChunks = byKind('screen');
  for (let i = 0; i < screenChunks.length; i += FRAME_FETCHES_AT_ONCE) {
    const batch = screenChunks.slice(i, i + FRAME_FETCHES_AT_ONCE);
    const bodies = await Promise.all(batch.map(fetchChunk));
    batch.forEach((c, j) => {
      const body = bodies[j];
      if (body)
        frames.push({
          n: c.n,
          blob: new Blob([body as BlobPart], { type: 'image/jpeg' }),
        });
    });
  }

  // Anything else the manifest lists is checked too, so "every chunk" means every chunk.
  const known = new Set(['events', 'audio', 'screen']);
  await Promise.all(
    manifest.chunks.filter((c) => !known.has(c.kind)).map(fetchChunk),
  );

  const chunkTotal = manifest.chunks.length;
  return {
    replay,
    manifest,
    audio,
    frames,
    verification: {
      verified:
        view.signature !== null &&
        listsAgree &&
        matched === chunkTotal &&
        !replay.damaged,
      manifestSha256,
      matched,
      total: chunkTotal,
    },
  };
}
